import { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import PolicyPipelineDiagram from '../../../components/policies/PolicyPipelineDiagram';
import { routingPolicyService } from '../../../services';
import type { RoutingPolicy } from '../../../services';

interface RoutingPoliciesSummaryProps {
  edgeId: string;
  tenantId?: string;
}

const RoutingPoliciesSummary = ({ edgeId, tenantId }: RoutingPoliciesSummaryProps) => {
  const [loading, setLoading] = useState(true);
  const [assignedPolicies, setAssignedPolicies] = useState<RoutingPolicy[]>([]);

  useEffect(() => {
    fetchPolicies();
  }, [edgeId]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const allPolicies = await routingPolicyService.list({ pageSize: 100 });

      // Policies assigned to this edge
      const assigned = allPolicies.items.filter(p => p.edge_id === edgeId);
      setAssignedPolicies(assigned);
    } catch (error) {
      console.error('Failed to fetch routing policies:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">VPP Traffic Processing Pipeline</p>
            <p className="mt-1">
              This diagram shows how traffic is processed through the VPP dataplane on this edge.
              Traffic flows through each stage sequentially — only explicit drop rules terminate processing early.
              Click any box to expand details and access configuration.
            </p>
            <Link
              to={`/edges/${edgeId}/config`}
              className="inline-flex items-center text-blue-800 hover:text-blue-900 font-medium mt-2"
            >
              Configure Edge
              <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* Traffic Processing Pipeline */}
      <PolicyPipelineDiagram
        edgeId={edgeId}
        tenantId={tenantId}
        assignedPolicies={assignedPolicies}
      />
    </div>
  );
};

export default RoutingPoliciesSummary;
